// RELEASE — a deposit KYT sent to Merchant Hot moves to Hot, signed on his Ledger by cable, one deposit at a time (N-099, his
// "yes ofc", 2 Nov 2026). The route and the row's `release` are the wallet session's (POST /api/custody/deposits/:id/release;
// the /deposits row's latest release); this file is the button and its states only.
//
// THE BUTTON NEVER CALLS A ROUTE THAT IS NOT THERE: it shows only when the row CARRIES `release` (main 4a2e51ba; a Worker
// before it sends no such key), and only for a swept Merchant Hot deposit with no release under way (or a failed one). It is
// off while a press is in flight or the desk window is closed. Its states, one line each: Release requested → Waiting on you
// (the card) → Sending → Released, in Hot; a lapsed card waits for the next window; a failed one says so and offers the button
// again. A refusal says the desk's own line, else words for its reason (screens/custody/refusals.ts releaseRefusal).
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api as books, custody as api, type DepositRow } from '../../../lib/api';
import { ApiError } from '../../../lib/errors';
import { readState } from '../../../lib/read-state';
import { readIsCurrent, windowOpenUntil } from '../../../lib/custody-screen';
import { releaseRefusal } from '../../../screens/custody/refusals';
import { ReadFailed, StateLine } from '../kit';

/** May this deposit be released now (the wallet's rule)? A row WITHOUT the field (a Worker before 4a2e51ba) never may. */
export function canRelease(d: DepositRow): boolean {
  return d.release !== undefined && d.pool === 'dirty' && d.status === 'swept' && (d.release === null || d.release.status === 'failed');
}

/** The release's state, as one line, and the link to its card when it waits on him. */
export function ReleaseState({ d }: { d: DepositRow }) {
  const r = d.release;
  if (!r) return null;
  if (r.status === 'queued' || r.status === 'activating') return <StateLine>Release requested</StateLine>;
  if (r.status === 'awaiting_approval') {
    return r.card ? <StateLine tone="warn" action={<Link to={`/custody/approvals/${r.card.txid}`} className="btn-link text-body">Sign</Link>}>Release waits on you</StateLine>
      : <StateLine tone="warn">Release waits on you</StateLine>;
  }
  if (r.status === 'expired') return <StateLine tone="warn">Card lapsed — rebuilt at the next desk window</StateLine>;
  if (r.status === 'energizing' || r.status === 'broadcast') return <StateLine>Sending to Hot</StateLine>;
  if (r.status === 'swept') return <StateLine tone="good">Released, in Hot</StateLine>;
  if (r.status === 'failed') return <StateLine tone="bad">Release failed — press Release again</StateLine>;
  return <StateLine>Release: {r.status}</StateLine>;
}

/** A press's key: one per press (the wallet's idempotency, as /moves) — 8–64 of A-Z a-z 0-9 _ -. */
export const newReleaseKey = () => (globalThis.crypto?.randomUUID?.() ?? `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`).replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64);

/** WHEN THE KEY TURNS OVER (Astra, 2 Nov 2026): after a DEFINITIVE answer — the release was recorded, or the desk refused it in
 *  words (any 4xx: request_voided, no_open_window, a cap …) — the next press is a new press with a new key. After an UNCERTAIN
 *  one — no answer, a timeout, a 5xx — the key stays, so pressing again replays the same press and can never book twice. */
export function keyAfter(err: unknown | null): 'new' | 'keep' {
  if (err === null) return 'new';
  return err instanceof ApiError && err.status >= 400 && err.status < 500 ? 'new' : 'keep';
}

/** `known`: the deposit's own read is current (a cached row after a failed refresh may no longer be eligible). */
export function ReleaseButton({ d, known }: { d: DepositRow; known: boolean }) {
  const qc = useQueryClient();
  const key = useRef(newReleaseKey());
  const me = useQuery({ queryKey: ['me'], queryFn: () => books.me() });
  const win = useQuery({ queryKey: ['custody', 'approval-window'], queryFn: api.approvalWindow, refetchInterval: 30_000, retry: false });
  // A MONEY DOOR NEEDS A CURRENT READ of the window: a cached "open" after a failed or paused refresh is not open.
  const winKnown = readState(win) === 'known' && readIsCurrent(win);   // a PAUSED refresh (offline) is not current either
  const open = winKnown && windowOpenUntil(win.data) !== null;
  const m = useMutation({
    mutationFn: (requestId: string) => api.releaseDeposit(d.id, requestId),
    onSettled: (_r, err) => { if (keyAfter(err ?? null) === 'new') key.current = newReleaseKey(); },
    onSuccess: () => { for (const k of ['deposits', 'approvals', 'balances']) void qc.invalidateQueries({ queryKey: ['custody', k] }); },
  });
  // Full access only: a viewer sees no button (the route answers 403 to one anyway).
  if (!canRelease(d) || me.data?.role === 'read_only' || (m.error instanceof ApiError && m.error.status === 403)) return null;
  return (
    <span className="flex flex-col items-end gap-2">
      {m.isError && <StateLine tone="bad">{releaseRefusal(m.error)}</StateLine>}
      {win.isError && <ReadFailed what="the desk window" error={win.error} onRetry={() => void win.refetch()} tone="bad" />}
      {!known && <span className="text-meta text-tg-hint">Read the deposits again to release</span>}
      {winKnown && !open && <span className="text-meta text-tg-hint">Open the desk window to release</span>}
      <button type="button" className="btn-primary whitespace-nowrap px-5 text-body" disabled={m.isPending || !open || !known} onClick={() => { if (known && open) m.mutate(key.current); }}>
        {m.isPending ? 'Releasing…' : 'Release to Hot'}
      </button>
    </span>
  );
}
